import { CardDataInterface } from "@tm/api-utils";
import { TextNodeType } from "../../FormattedText/FormattedText";

import { ApiBenefit, transformBenefits } from "./BenefitsTransformer";
import { InsuranceListItemProps } from "./types";

/**
 * Insurance-specific API types
 */
export interface ApiInsuranceCard {
  quoteId: string;
  productId: string;
  insIcon: string;
  insPlanName: TextNodeType;
  insProvider: TextNodeType;
  persuasion?: {
    title: TextNodeType;
  };
  tnc?: {
    title: TextNodeType;
    link: string;
  };
  paxPerDay?: TextNodeType;
  buyBtn?: TextNodeType;
  insAmount: TextNodeType;
  originalPrice?: TextNodeType;
  isPopular?: boolean;
  popularTag?: TextNodeType;
  benefits_info: {
    insBenefits: ApiBenefit[];
    downloadBenefitsModal?: {
      header?: {
        title?: TextNodeType;
      };
      content?: {
        description?: TextNodeType;
        options?: {
          id?: string;
          label?: TextNodeType;
          defaultSelected?: boolean;
          templateType?: string; // Maps to "with_price" or "without_price"
        }[];
      };
      footer?: {
        buttons?: {
          id?: string;
          text?: TextNodeType;
          type?: string;
          buttonStyles?: {
            backgroundColor?: string;
            border?: string;
            padding?: string;
            borderRadius?: string;
          };
        }[];
      };
    };
    bottomsheet?: {
      benefitItemElementLayout?: {
        id?: string;
        icon?: string;
        title?: TextNodeType;
        info?: TextNodeType;
        price?: TextNodeType;
      };
      moreBenefits?: {
        [key: string]:
          | {
              id?: string;
              icon?: string;
              title?: TextNodeType;
              info?: TextNodeType;
              price?: TextNodeType;
            }[]
          | undefined;
      };
      totalBenefits?: string;
      titleDownloadBenefit?: TextNodeType;
      disclaimer?: string;
      PowerBy?: TextNodeType;
      insPlanName?: TextNodeType;
      insProvider?: TextNodeType;
      tnc?: {
        title?: TextNodeType;
        link?: string;
      };
      downloadBenefitsDropdown?: {
        header?: {
          title?: TextNodeType;
        };
        options?: {
          id?: string;
          label?: TextNodeType;
          defaultSelected?: boolean;
          templateType?: string; // Maps to "with_price" or "without_price"
        }[];
      };
    };
  };
}

export interface ApiComponentData {
  cards: ApiInsuranceCard[];
}

export interface InsuranceCallbacks {
  onMoreBenefits?: (quoteId: string) => void;
}

/**
 * Transforms bottomsheet data into MoreBenefits data structure
 */
export const transformBottomsheetToMoreBenefits = (
  apiCard: ApiInsuranceCard,
  insAmount?: TextNodeType,
  paxPerDay?: TextNodeType,
  buyBtn?: string,
  planTitle?: TextNodeType
): CardDataInterface => {
  const bottomsheet = apiCard.benefits_info.bottomsheet;

  if (!bottomsheet) {
    throw new Error(
      "Bottomsheet data is required for MoreBenefits transformation"
    );
  }

  return {
    productId: apiCard.productId || "",
    quoteId: apiCard.quoteId || "",
    vendorDisplayPrefix:
      (typeof bottomsheet.PowerBy?.text === "object" &&
      !Array.isArray(bottomsheet.PowerBy.text)
        ? (bottomsheet.PowerBy.text as any).value
        : bottomsheet.PowerBy?.text) || "Powered by",
    insuranceType: bottomsheet.insPlanName,
    service: bottomsheet.disclaimer,
    planExtraInfo: undefined, // Not available in new structure
    packName: bottomsheet.titleDownloadBenefit,
    ctaLink: undefined, // Not available in new structure
    tncLink: bottomsheet.tnc?.link,
    links: [
      // Add T&C link if available
      ...(bottomsheet.tnc
        ? [
            {
              id: "tnc",
              title: bottomsheet.tnc.title,
              type: "link" as const,
              url: bottomsheet.tnc.link,
            },
          ]
        : []),
      // Add download benefits dropdown if available
      ...(bottomsheet.downloadBenefitsDropdown
        ? [
            {
              id: "download_benefits",
              title: bottomsheet.downloadBenefitsDropdown.header?.title || {
                text: { value: "Download Benefits" },
              },
              type: "dropdown" as const,
              dropdownOptions:
                bottomsheet.downloadBenefitsDropdown.options?.map((option) => ({
                  title: option.label || { text: { value: "" } },
                  url: "", // No longer using URLs from mock data
                  templateType: option.id || "with_price", // Use option.id as templateType
                  ctaText: { text: { value: "DOWNLOAD NOW" } },
                  defaultSelected: option.defaultSelected || false,
                  id: option.id || `template-${option.id || "with_price"}`,
                })) || [],
            },
          ]
        : []),
    ],
    totalBenefits: bottomsheet.totalBenefits,
    moreBenefits: bottomsheet.moreBenefits,
    insAmount,
    paxPerDay,
    buyBtn,
    insPlanName: bottomsheet.insProvider,
  } as CardDataInterface;
};

/**
 * Transforms API insurance card data into InsuranceListItem props
 */
export const transformInsuranceCard = (
  apiCard: ApiInsuranceCard,
  onMoreBenefits?: (quoteId: string) => void
): InsuranceListItemProps => {
  const transformedBenefits = transformBenefits(
    apiCard.benefits_info.insBenefits
  );

  // Calculate buyBtn value once
  const buyBtnValue =
    typeof apiCard.buyBtn === "string"
      ? apiCard.buyBtn
      : (apiCard.buyBtn as any)?.text?.value || "BUY NOW";

  const transformedCard = {
    quoteId: apiCard.quoteId, // Include quoteId for filtering
    productId: apiCard.productId, // Include productId for navigation
    logoUrl: apiCard.insIcon,
    companyName: apiCard.insProvider,
    planTitle: apiCard.insPlanName,
    discountedPrice: apiCard.insAmount,
    originalPrice: apiCard.originalPrice,
    perPersonText: apiCard.paxPerDay,
    discountText: apiCard.persuasion?.title,
    benefits: transformedBenefits,
    moreBenefitsText: {
      text: {
        value: `${
          apiCard.benefits_info.bottomsheet?.totalBenefits || "More Benefits"
        }`,
      },
    },
    buyButtonText: apiCard.buyBtn,
    onMoreBenefits: onMoreBenefits
      ? () => onMoreBenefits(apiCard.quoteId)
      : undefined,

    downloadBenefitsModal: apiCard.benefits_info.downloadBenefitsModal,
    bottomsheet: apiCard.benefits_info.bottomsheet,
    insAmount: apiCard.insAmount,
    paxPerDay: apiCard.paxPerDay,
    buyBtn: buyBtnValue,
    moreBenefitsTitle: `${
      apiCard.benefits_info.bottomsheet?.totalBenefits || "More Benefits"
    }`,
    // Pre-transform MoreBenefits data to avoid inline transformation
    moreBenefitsData: apiCard.benefits_info.bottomsheet
      ? transformBottomsheetToMoreBenefits(
          apiCard,
          apiCard.insAmount,
          apiCard.paxPerDay,
          buyBtnValue,
          apiCard.insPlanName
        )
      : undefined,
  };

  return transformedCard;
};

/**
 * Transforms API data into PlanCardsList props (matches ComponentMapper pattern)
 * This function returns the exact props structure that PlanCardsList expects
 */
export const transformInsuranceCards = (
  apiData: unknown
): { data: InsuranceListItemProps[] } => {
  const typedApiData = apiData as ApiComponentData;

  if (!typedApiData?.cards) {
    return { data: [] };
  }

  const transformedCards = typedApiData.cards.map((card) => {
    return transformInsuranceCard(card);
  });

  return { data: transformedCards };
};

/**
 * Transforms multiple API insurance cards with callbacks (for direct use)
 */
export const transformInsuranceCardsWithCallbacks = (
  apiData: ApiComponentData,
  callbacks?: InsuranceCallbacks
): InsuranceListItemProps[] => {
  return apiData.cards.map((card) =>
    transformInsuranceCard(card, callbacks?.onMoreBenefits)
  );
};
